import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { cn } from '@/shared/lib/cn';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { CaeFacturaPreview } from '@/features/administracion/components/CaeFacturaPreview';
import {
  solicitarNotaCredito,
  type SolicitarCaeResponse,
} from '@/features/administracion/api';
import { downloadFacturaArcaPdf, buildFacturaArcaPdfFile } from '@/features/administracion/pdf/facturaArca';
import { uploadDocument } from '@/shared/api/documents';

type FacturadoResult = Extract<SolicitarCaeResponse, { outcome: 'facturado' }>;

/**
 * Notas de Crédito parciales: rehabilitadas 2026-07-21 a pedido del cliente.
 * Una NC parcial es un DESCUENTO: la venta termina facturada por menos, el
 * monto acreditado NO vuelve al saldo a facturar (ver `VerFacturasDialog`).
 * La factura conserva su CAE y sigue en Facturación; sólo la NC por el total
 * reabre el cobro.
 *
 * Tiene un flag gemelo en el backend (`NC_PARCIAL_HABILITADA` en
 * `server/src/routes/administracion.ts`), que rechaza el `montoNC` aunque el
 * front lo mande. Los dos tienen que estar en `true`.
 */
const NC_PARCIAL_HABILITADA = true;

/**
 * Genera una Nota de Crédito (fiscal) para una factura ARCA ya emitida —
 * por el total o un monto parcial. Muestra el mismo preview que Administración
 * (dry-run) y emite el comprobante contra ARCA. No mueve caja ni cambia la
 * venta (decisión de producto: la NC es solo el comprobante fiscal).
 */
export function GenerarNotaCreditoDialog({
  facturaReceiptId,
  nroFactura,
  onClose,
}: {
  facturaReceiptId: string;
  nroFactura: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  // One idempotency key per dialog open → a double-submit collapses to one NC. Se renueva tras
  // una respuesta DEFINITIVA (saldo excedido / rechazo de ARCA): ese intento quedó anulado y un
  // reintento es una NC nueva. Con `in_progress` o un corte de red se conserva la misma.
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());
  // Candado síncrono contra el doble clic: `isPending` recién se refleja en el próximo render.
  const emitLock = useRef(false);
  // Paso 1 elegir total/parcial, paso 2 preview + emitir (pedido del cliente:
  // la elección va ANTES del preview, no como checkbox debajo).
  // Paso previo al preview: con las parciales habilitadas es la elección
  // total/parcial; con las parciales apagadas es la confirmación de que la NC
  // va por el total. En los dos casos se confirma con Continuar.
  const [step, setStep] = useState<'elegir' | 'preview'>('elegir');
  const [parcial, setParcial] = useState(false);
  const [montoStr, setMontoStr] = useState('');
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [result, setResult] = useState<FacturadoResult | null>(null);
  // Máximo acreditable (saldo restante de la factura) — aprendido del dry-run.
  const [maxCreditable, setMaxCreditable] = useState<number | null>(null);
  // NC PDF attach-to-drive (mirrors the factura CAE attach): store the
  // representación impresa on the NC row so it's recoverable later from
  // "Notas de Crédito" — not just the one-shot download at emit.
  const [ncAttach, setNcAttach] = useState<'idle' | 'attaching' | 'done' | 'failed'>('idle');
  const [ncAttachFailed, setNcAttachFailed] = useState(false);

  const attachNcPdf = async (r: FacturadoResult) => {
    if (!r.comprobanteId) return; // sin id de fila no hay dónde adjuntar
    setNcAttach('attaching');
    try {
      const file = await buildFacturaArcaPdfFile(r.factura);
      let lastErr: unknown;
      for (let i = 0; i < 3; i++) {
        try {
          await uploadDocument({ entityType: 'NotaCreditoVenta', entityId: r.comprobanteId, file });
          qc.invalidateQueries({ queryKey: ['pagos'] });
          setNcAttach('done');
          return;
        } catch (err) {
          lastErr = err;
        }
      }
      throw lastErr;
    } catch (err) {
      console.warn('[solicitar-nc] auto-attach PDF failed', err);
      setNcAttachFailed(true);
      setNcAttach('failed');
    }
  };

  const montoParsed = parseMoney(montoStr) ?? undefined;
  // En el paso "elegir" el dry-run corre SIEMPRE por el total: así aprendemos el
  // máximo acreditable y detectamos bloqueos (sin CAE, datos fiscales faltantes)
  // antes de que el usuario elija. Recién en el preview se pide el monto parcial.
  const montoNC =
    NC_PARCIAL_HABILITADA && step === 'preview' && parcial ? montoParsed : undefined;
  const montoValido = montoParsed != null && montoParsed > 0;
  const overMax =
    parcial && montoParsed != null && maxCreditable != null && montoParsed > maxCreditable + 0.01;

  const dryQ = useQuery({
    queryKey: ['nc-dryrun', facturaReceiptId, montoNC ?? 'full'],
    queryFn: () =>
      solicitarNotaCredito({
        facturaReceiptId,
        idempotencyKey: idemKey,
        montoNC,
        dryRun: true,
      }),
    enabled: !result,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const check = dryQ.data;
  // Una Factura E se acredita en dólares (saldo y monto parcial incluidos).
  const cur: 'ARS' | 'USD' =
    result?.factura.exportacion != null ||
    ((check?.outcome === 'dry_run' || check?.outcome === 'nc_saldo_excedido') && check.monedaIso === 'USD')
      ? 'USD'
      : 'ARS';
  const preview = check?.outcome === 'dry_run' ? check : null;
  const saldoError = check?.outcome === 'nc_saldo_excedido' ? check : null;
  const blockedMsg =
    check && check.outcome !== 'dry_run' && check.outcome !== 'nc_saldo_excedido' && 'message' in check
      ? check.message
      : check?.outcome === 'missing_fiscal_data'
        ? check.missing.map((m) => m.message).join(' · ')
        : null;

  // Learn the remaining saldo: the full-credit dry-run's total is the saldo
  // disponible, and a saldo-excedido response reports it directly.
  useEffect(() => {
    if (check?.outcome === 'nc_saldo_excedido') setMaxCreditable(check.disponible);
    else if (check?.outcome === 'dry_run' && montoNC == null)
      setMaxCreditable(check.resumen.importes.total);
  }, [check, montoNC]);

  const emit = useMutation({
    mutationFn: () =>
      solicitarNotaCredito({
        facturaReceiptId,
        idempotencyKey: idemKey,
        montoNC,
        lineasTexto: preview?.lineas.map((l, i) => edits[i] ?? l.articulo),
      }),
    onSuccess: async (r) => {
      if (r.outcome === 'facturado') {
        setResult(r);
        // A full NC reopens the original cobro — refresh the facturas lists.
        // Invalidate the whole `['pagos']` prefix (NOT `['pagos','comprobantes']`)
        // so it ALSO covers VerFacturasDialog's NC-netting query
        // `['pagos','comprobantes-full', ventaId]` — a stricter key does NOT
        // prefix-match it (element[1] differs), so after a PARTIAL NC the
        // "saldo a facturar" banner would show stale numbers until its 30s
        // staleTime lapses. Partials are enabled here (NC_PARCIAL_HABILITADA=true).
        qc.invalidateQueries({ queryKey: ['pagos'] });
        qc.invalidateQueries({ queryKey: ['detalle-venta'] });
        // La NC es justamente lo que saca a la factura del aviso de "facturas
        // sin venta activa" (el aviso filtra por `saldo > 0`). Sin esto el
        // operador vuelve a Home y sigue viendo la factura que acaba de
        // acreditar, así que emite una segunda NC.
        qc.invalidateQueries({ queryKey: ['facturacion'] });
        // Store the PDF on the NC row (recoverable later) AND auto-download a
        // local copy. Attach is best-effort with a retry/manual fallback below.
        void attachNcPdf(r);
        try {
          await downloadFacturaArcaPdf(r.factura);
        } catch {
          /* el PDF se puede volver a descargar desde la pantalla de éxito */
        }
      } else if (r.outcome === 'nc_saldo_excedido') {
        setIdemKey(crypto.randomUUID());
        toast.error('Saldo excedido', { description: r.message });
      } else if (r.outcome === 'afip_rejected') {
        setIdemKey(crypto.randomUUID());
        toast.error('ARCA rechazó la Nota de Crédito', { description: r.message, duration: 12000 });
      } else if (r.outcome === 'missing_fiscal_data') {
        toast.error('Faltan datos fiscales', {
          description: r.missing.map((m) => m.message).join(' · '),
        });
      } else if ('message' in r) {
        toast.info('No se pudo emitir', { description: r.message });
      }
    },
    onSettled: () => {
      emitLock.current = false;
    },
    onError: (e) =>
      toast.error('No se pudo emitir la Nota de Crédito', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      }),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && !emit.isPending && onClose()}>
      {/* Sólo el preview necesita ancho (evita que el detalle scrollee); el paso
          de elección y el resumen final quedan compactos. */}
      <DialogContent className={!result && step === 'preview' ? 'max-w-6xl' : 'max-w-lg'}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {result ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
            )}
            {result ? 'Nota de Crédito emitida' : 'Generar Nota de Crédito'}
          </DialogTitle>
          <DialogDescription>
            {result
              ? `NC ${result.nroComprobante} sobre la factura ${nroFactura}.`
              : step === 'elegir'
                ? NC_PARCIAL_HABILITADA
                  ? `Elegí si la Nota de Crédito acredita el total de la factura ${nroFactura} o sólo una parte.`
                  : `Revisá antes de continuar: la Nota de Crédito se emite por el total de la factura ${nroFactura}.`
                : `Se emitirá un comprobante fiscal real en ARCA que acredita la factura ${nroFactura}. Una vez emitido no se puede deshacer.`}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 px-6 pb-2 text-sm">
            <div className="space-y-1.5 rounded-md border bg-muted/40 px-3 py-2 tabular-nums">
              <Row label="Comprobante" value={result.nroComprobante} />
              <Row label="CAE" value={result.cae} />
              <Row label="Vto. CAE" value={result.vencimiento ?? '—'} />
              <Row label="Total acreditado" value={formatMoney(result.factura.importes.total, cur)} />
            </div>
            {result.facturaReabierta && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Se acreditó el total de la factura, así que el cobro quedó{' '}
                <strong>pendiente de facturar</strong>. Emití la factura correcta por ARCA o subí
                una nueva.
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              El PDF se descargó automáticamente. Si no, volvé a descargarlo.
            </p>
            {ncAttach === 'attaching' && !ncAttachFailed && (
              <p className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                Adjuntando el PDF al sistema…
              </p>
            )}
            {ncAttach === 'done' && (
              <p className="flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
                PDF adjuntado al sistema.
              </p>
            )}
            {ncAttachFailed && ncAttach !== 'done' && (
              <div className="space-y-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <p>No se pudo adjuntar el PDF al sistema automáticamente (lo descargaste igual).</p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={ncAttach === 'attaching'}
                  onClick={() => {
                    if (result) void attachNcPdf(result);
                  }}
                >
                  {ncAttach === 'attaching' ? 'Adjuntando…' : 'Reintentar adjuntar'}
                </Button>
              </div>
            )}
          </div>
        ) : step === 'elegir' ? (
          /* Paso 1 — total o parcial. El dry-run por el total ya corrió, así que
             acá mostramos el máximo acreditable y cualquier bloqueo. */
          <div className="flex flex-col gap-3 px-6 pb-2">
            {saldoError && (
              <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
                {saldoError.message} (disponible {formatMoney(saldoError.disponible, cur)} de{' '}
                {formatMoney(saldoError.facturaTotal, cur)}).
              </div>
            )}
            {blockedMsg && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                {blockedMsg}
              </div>
            )}
            {dryQ.isFetching && !preview && (
              <div className="flex h-16 items-center justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              </div>
            )}

            {NC_PARCIAL_HABILITADA ? (
              <>
                <OpcionNC
                  selected={!parcial}
                  title="Nota de Crédito por el total"
                  description={
                    maxCreditable != null
                      ? `Se acredita el importe completo (${formatMoney(maxCreditable, cur)}): la factura se reabre y el cobro vuelve a quedar pendiente de facturar.`
                      : 'Se acredita el importe completo: la factura se reabre y el cobro vuelve a quedar pendiente de facturar.'
                  }
                  onSelect={() => setParcial(false)}
                />
                <OpcionNC
                  selected={parcial}
                  title="Nota de Crédito parcial"
                  description="Acredita sólo una parte: funciona como descuento, la venta queda facturada por menos y el saldo a facturar no cambia."
                  onSelect={() => setParcial(true)}
                />
              </>
            ) : (
              // Sin elección posible: este paso es el recordatorio de qué va a
              // pasar, antes de mostrar el preview del comprobante.
              <>
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm leading-relaxed text-amber-900">
                  <p>
                    La Nota de Crédito se emite por el <strong>total</strong> del saldo de la
                    factura
                    {maxCreditable != null ? (
                      <>
                        {': '}
                        <strong>{formatMoney(maxCreditable, cur)}</strong>.
                      </>
                    ) : (
                      '.'
                    )}
                  </p>
                  <p className="mt-1.5">
                    Al acreditarse el total, la factura <strong>se reabre</strong>: pierde su CAE y
                    deja de figurar en Facturación, y el cobro vuelve a quedar sin facturar.{' '}
                    <strong>No</strong> se agrega un comprobante nuevo al listado de Facturación.
                  </p>
                </div>
                <p className="px-1 text-xs text-muted-foreground">
                  Las Notas de Crédito parciales están temporalmente deshabilitadas.
                </p>
              </>
            )}

            {parcial && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
                <Label htmlFor="nc-monto" className="font-normal text-muted-foreground">
                  Monto a acreditar ({cur})
                </Label>
                <Input
                  id="nc-monto"
                  value={montoStr}
                  onChange={(e) => setMontoStr(e.target.value)}
                  placeholder="0,00"
                  autoFocus
                  aria-invalid={overMax}
                  className="h-8 w-40 text-sm aria-[invalid=true]:border-red-500"
                />
                {maxCreditable != null && (
                  <span
                    className={overMax ? 'text-xs text-red-600' : 'text-xs text-muted-foreground'}
                  >
                    {overMax ? 'Supera el máximo. ' : ''}Máximo a acreditar:{' '}
                    {formatMoney(maxCreditable, cur)}
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="flex max-h-[74vh] flex-col gap-3 overflow-y-auto px-6 pb-2">
            {saldoError && (
              <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
                {saldoError.message} (disponible {formatMoney(saldoError.disponible, cur)} de{' '}
                {formatMoney(saldoError.facturaTotal, cur)}).
              </div>
            )}
            {blockedMsg && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                {blockedMsg}
              </div>
            )}
            {check?.outcome === 'dry_run' && check.production === false && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">
                ⚠ Modo HOMOLOGACIÓN (prueba). El CAE no tiene validez fiscal.
              </div>
            )}

            {dryQ.isFetching && !preview && (
              <div className="flex h-24 items-center justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              </div>
            )}

            {preview && (
              <CaeFacturaPreview
                resumen={preview.resumen}
                lineas={preview.lineas}
                fecha={preview.fecha}
                edits={edits}
                onEditLine={(i, text) => setEdits((prev) => ({ ...prev, [i]: text }))}
                disabled={emit.isPending}
              />
            )}

          </div>
        )}

        <DialogFooter>
          {result ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => downloadFacturaArcaPdf(result.factura)}
              >
                Descargar PDF
              </Button>
              <Button type="button" onClick={onClose}>
                Cerrar
              </Button>
            </>
          ) : step === 'elegir' ? (
            <>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => setStep('preview')}
                // Sin `preview` la factura no es emitible (sin CAE, datos fiscales
                // faltantes, saldo agotado): el dry-run por el total ya lo dijo.
                // En parcial además exigimos un monto > 0 y dentro del máximo.
                disabled={
                  dryQ.isFetching || !preview || (parcial && (!montoValido || overMax))
                }
              >
                Continuar
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep('elegir')}
                disabled={emit.isPending}
              >
                Atrás
              </Button>
              <Button
                type="button"
                onClick={() => {
                  if (emitLock.current) return;
                  emitLock.current = true;
                  emit.mutate();
                }}
                // `overMax` blocks an amount above the factura's remaining saldo
                // (instant, before the server's saldo-excedido round-trip).
                disabled={emit.isPending || dryQ.isFetching || !preview || overMax}
              >
                {emit.isPending ? 'Emitiendo…' : 'Emitir Nota de Crédito'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Tarjeta seleccionable del paso 1 (total vs parcial). */
function OpcionNC({
  selected,
  title,
  description,
  onSelect,
}: {
  selected: boolean;
  title: string;
  description: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex items-start justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
        selected ? 'border-foreground bg-foreground/5' : 'border-border hover:bg-secondary',
      )}
    >
      <span>
        <span className="block text-sm font-medium text-foreground">{title}</span>
        <span className="block text-xs text-muted-foreground">{description}</span>
      </span>
      {selected && <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />}
    </button>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium text-foreground">{value}</span>
    </div>
  );
}
